import React, { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { useToast } from "../../hooks/use-toast";

const ContactSection = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    company: "",
    message: "",
    consent: false
  });

  const { toast } = useToast();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleConsentChange = (checked) => {
    setFormData(prev => ({
      ...prev,
      consent: checked
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.consent) {
      toast({
        title: "Consent Required",
        description: "Please consent to receive marketing messages.",
        variant: "destructive"
      });
      return;
    }

    // Mock form submission
    console.log("Form submitted:", formData);
    
    toast({
      title: "Message Sent!",
      description: "We'll get back to you within 24 hours.",
    });

    // Reset form
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      company: "",
      message: "",
      consent: false
    });
  };

  return (
    <section id="contact" className="py-20 bg-black">
      <div className="container mx-auto px-6">
        <div className="max-w-2xl mx-auto">
          {/* Section Title */}
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Contact Us
            </h2>
            <p className="text-gray-300 text-lg">
              Ready to transform your real estate business? Get in touch with us today.
            </p>
          </div>

          {/* Contact Form */}
          <form onSubmit={handleSubmit} className="bg-gray-900 border border-gray-700 rounded-lg p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="firstName" className="block text-white text-sm font-medium mb-2">
                  First Name *
                </label>
                <Input
                  id="firstName"
                  name="firstName"
                  type="text"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  className="bg-black border-gray-600 text-white focus:border-yellow-400"
                  required
                />
              </div>
              <div>
                <label htmlFor="lastName" className="block text-white text-sm font-medium mb-2">
                  Last Name *
                </label>
                <Input
                  id="lastName"
                  name="lastName"
                  type="text"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className="bg-black border-gray-600 text-white focus:border-yellow-400"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="email" className="block text-white text-sm font-medium mb-2">
                  Email Address *
                </label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="bg-black border-gray-600 text-white focus:border-yellow-400"
                  required
                />
              </div>
              <div>
                <label htmlFor="phone" className="block text-white text-sm font-medium mb-2">
                  Phone Number
                </label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="bg-black border-gray-600 text-white focus:border-yellow-400"
                />
              </div>
            </div>

            <div className="mb-6">
              <label htmlFor="company" className="block text-white text-sm font-medium mb-2">
                Company/Agency Name
              </label>
              <Input
                id="company"
                name="company"
                type="text"
                value={formData.company}
                onChange={handleInputChange}
                className="bg-black border-gray-600 text-white focus:border-yellow-400"
              />
            </div>

            <div className="mb-6">
              <label htmlFor="message" className="block text-white text-sm font-medium mb-2">
                Message
              </label>
              <Textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                className="bg-black border-gray-600 text-white focus:border-yellow-400 min-h-[120px]"
                placeholder="Tell us about your real estate business and how we can help..."
              />
            </div>

            <div className="flex items-start space-x-3 mb-8">
              <Checkbox
                id="consent"
                checked={formData.consent}
                onCheckedChange={handleConsentChange}
                className="mt-1 border-gray-600 data-[state=checked]:bg-yellow-400 data-[state=checked]:border-yellow-400"
              />
              <label htmlFor="consent" className="text-gray-300 text-sm leading-relaxed">
                By checking this box, I consent to receive marketing and promotional messages, including special offers, discounts, new product updates among others.
              </label>
            </div>

            <Button
              type="submit"
              className="w-full bg-yellow-400 hover:bg-yellow-500 text-black font-semibold py-3 text-lg transition-all duration-300 transform hover:scale-105"
            >
              Submit
            </Button>
          </form>

          {/* Privacy Link */}
          <div className="text-center mt-6">
            <a href="#privacy" className="text-gray-400 hover:text-yellow-400 text-sm transition-colors">
              Privacy Policy | Terms of Service
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;